import { Calendar, CalendarTags } from "@/components/Calendar";
import { Dropdown } from "@/components/Dropdown";
import { Loading } from "@/components/Loading";
import { SalesClosedModal } from "@/components/Modal/SalesClosedModal";
import { useModal } from "@/hooks/useModal";
import { useCalendar } from "@/hooks/useCalendar";
import {
    useCalendarQuery,
    useSalesStatusQuery,
    useSchedulesQuery,
} from "@/hooks/useCalendarQuery";
import { getStringDate, smallDateFormatter } from "@/utils";
import dayjs from "@/utils/dayjs";
import { AnimatePresence, motion, MotionProps } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    LuCalendar,
    LuChevronLeft,
    LuChevronRight,
    LuClock,
} from "react-icons/lu";
import { NavLink } from "react-router";
import { toast } from "react-toastify";
import styled from "styled-components";

export const Backdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 11;
    backdrop-filter: blur(5px) brightness(0.5) grayscale(0.5);

    display: flex;
`;
const ModalContainer = styled.div`
    position: relative;
    padding: 1.5rem;
    background-color: white;

    width: 100%;
    height: 100%;
    max-width: 40rem;
    height: fit-content;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    border-radius: 1.5rem;

    h1 {
        color: var(--Neutral-950, #09090b);
        font-feature-settings: "liga" off, "clig" off;
        font-family: Poppins;
        font-size: 1.5rem;
        font-style: normal;
        font-weight: 600;
        line-height: 1.5rem; /* 100% */
    }

    .buttons-wrapper {
        display: flex;
        justify-content: stretch;
        width: 100%;
        gap: 1rem;

        button {
            width: 50%;
            padding: 0.75rem 1.25rem;
            border-radius: 0.75rem;
            border: 2px solid var(--BLUE, #195da5);
            color: var(--BLUE, #195da5);
            font-family: "Professor Marbles 3";
            font-size: 1.25rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.5rem; /* 120% */

            &.blue-background {
                background-color: #195da5;
                color: white;
            }

            --hover-scale: 1.05;
            --active-scale: 0.95;
        }
    }

    .tags-wrapper {
        width: 100%;
        justify-content: space-around;
        .tag {
            width: fit-content;
        }
    }
`;

const ReactCalendarWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;

    width: 100%;
    gap: 0.63rem !important;

    .react-calendar__viewContainer {
        padding-top: 0;
    }

    .tags-wrapper {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        > button {
            flex: 1 1 calc(50% - 0.25rem);
            min-width: calc(50% - 0.25rem);
            padding: 0.45rem 0.5rem;
            pointer-events: none;
            font-weight: 700;
            gap: 0.35rem;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            svg {
                width: 1.25rem;
                height: 1.25rem;
                flex-shrink: 0;
            }
        }

        > button.full-width {
            flex: 1 1 100%;
            min-width: 100%;
            width: 100%;
            justify-content: center;
        }
    }

    .dropdown-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.63rem;

        width: 100%;
        > * {
            flex-grow: 1;
        }

        > *:first-child {
            pointer-events: none;
        }
    }

    .line {
        width: 100%;
        height: 2px;
        border-radius: 0.1575rem;
        background: rgba(25, 93, 165, 0.2);
    }

    .react-calendar {
        display: flex;
        flex-direction: column;
        width: 100% !important;
    }
    .react-calendar__navigation {
        position: relative;
        box-shadow: none;
        border-radius: 0;
    }

    button.react-calendar__navigation__label span {
        color: var(--BLUE, #195da5);
        font-feature-settings: "liga" off, "clig" off;
        font-family: Poppins;
        font-size: 1rem;
        font-style: normal;
        font-weight: 700;
        line-height: 0.945rem; /* 120% */
    }

    .react-calendar__navigation__arrow svg {
        width: 2rem;
        height: 2rem;
        color: #195da5;
        /* stroke-width: 2.5; */
    }

    .react-calendar__month-view__weekdays__weekday {
        abbr {
            color: #195da5;
            font-family: "Poppins";
            font-size: 1.25rem;
            font-style: normal;
            font-weight: 700;
            line-height: 1.5rem;
        }
    }
    --gap: 0.5rem;

    .react-calendar__month-view__days {
        gap: var(--gap);
    }
    .react-calendar__tile--active {
        background-color: #00a0df !important;
    }

    .react-calendar__tile--now {
        background: #50af4a;
        &.react-calendar__tile--active {
            background-color: #00a0df !important;
        }
    }

    .react-calendar__tile,
    .react-calendar__tile--now {
        /* height: 2.205rem; */
        padding: 0;
        display: flex;
        justify-content: center;
        gap: 0.1rem;
        align-items: center;
        flex-direction: column;
        border-radius: 1rem;

        flex: 0 0 calc(100% / 7 - var(--gap)) !important;
        background: #50af4a;
        transition: 0.1s;
        --hover-scale: 1.05;
        --active-scale: 0.9;

        abbr {
            color: white;
            font-family: "Poppins";
            font-size: 1.5rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.5rem; /* 120% */
            font-family: "Professor Marbles 3";
        }

        &[disabled] {
            position: relative;
            background: white !important;
            cursor: not-allowed;

            abbr {
                color: #71717a;
            }
        }
    }

    .react-calendar__tile:hover {
        background: #47a041;
    }

    button.react-calendar__tile--active {
        color: white;
    }

    .price {
        color: #fff;
        font-family: Poppins;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 600;
        line-height: 1.2rem;
    }

    .cart-link-button {
        display: flex;
        justify-content: center;
        color: #fff;
        margin-top: 20px;
        padding: 0.25rem 1rem;
        padding-top: 0.5rem;
        border-radius: 1.32488rem;
        background: var(--cyan);

        font-family: "Professor Marbles 3";
        font-size: 2rem;
        font-weight: 400;
        letter-spacing: -0.15rem;
        text-align: center;
        text-decoration: none;
        transition: 0.2s;

        &:hover {
            transform: scale(1.05);
            background: var(--blue);
        }
    }
`;

export const fadeInOut: MotionProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
};

const injectScheduleIntoDate = (date: Date, time: string): Date => {
    if (!date || isNaN(date.getTime()) || !time) return date;

    const startTime = time.includes("-") ? time.split("-")[0].trim() : time.trim();
    const [rawHour = "0", rawMinute = "0", rawSeconds = "0"] = startTime.split(":");
    const hour = parseInt(rawHour, 10);
    const minute = parseInt(rawMinute, 10);
    const seconds = parseInt(rawSeconds, 10);

    const safeHour = isNaN(hour) ? 0 : hour;
    const safeMinute = isNaN(minute) ? 0 : minute;
    const safeSeconds = isNaN(seconds) ? 0 : seconds;

    const dateStr = getStringDate(date);
    const pad = (n: number) => String(n).padStart(2, "0");
    const timeStr = `${pad(safeHour)}:${pad(safeMinute)}:${pad(safeSeconds)}`;

    return dayjs.tz(`${dateStr} ${timeStr}`, "America/Sao_Paulo").toDate();
};

export const CalendarModalHeadless = ({
    initialDate,
    callback,
    className,
    cartButton = false,
    salesClosedPopup = false,
    clearInitialDateWhenSalesClosed = false,
}: {
    initialDate: Date | null;
    callback: (date: Date | null) => void;
    className?: string;
    cartButton?: boolean;
    // true: mostra modal ao clicar em uma data bloqueada; false: mostra toast.
    salesClosedPopup?: boolean;
    // Na Home, limpa a seleção inicial se hoje estiver bloqueado.
    clearInitialDateWhenSalesClosed?: boolean;
}) => {
    const calendarStore = useCalendar();

    const [date, setDate] = useState<Date | null>(initialDate || null);
    const [viewDate, setViewDate] = useState<Date | null>(date);

    const startDateMonth = useMemo(() => {
        return viewDate || new Date();
    }, [viewDate]);

    const calendarQuery = useCalendarQuery({
        startDate: startDateMonth,
    });

    useEffect(() => {
        callback(date);
        setViewDate(date);
        calendarStore.update({ selectedSchedule: null });
    }, [date]);

    useEffect(() => {
        if (calendarStore.schedules?.length && !calendarQuery.isLoading && calendarStore.date) {
            const dateWithSchedule = injectScheduleIntoDate(
                calendarStore.date,
                calendarStore.schedules[0]
            );
            calendarStore.update({
                date: dateWithSchedule,
                selectedSchedule: calendarStore.schedules[0],
            });
        }
    }, [calendarQuery.data, calendarStore.schedules, calendarQuery.isLoading]);

    const getTileInfoByDate = (
        date: Date
    ): { active: boolean; season: number } | null => {
        if (!Array.isArray(calendarQuery.data)) return null;
        const result = calendarQuery.data.find((availableDate) => {
            const isInTheResponse =
                getStringDate(date) === getStringDate(availableDate.date);

            return isInTheResponse && availableDate.season > 0;
        });

        if (!result) return null;

        return {
            active: true,
            season: result.season,
        };
    };

    const getTileClassName = (date: Date): string => {
        if (!Array.isArray(calendarQuery.data)) return "";
        const tileInfo = getTileInfoByDate(date);

        const isYellow = tileInfo && tileInfo.season === 1;
        const isPurple = tileInfo && tileInfo.season === 2;
        const isGreen = tileInfo && tileInfo.season === 3;

        if (isYellow) return "animated yellow";
        if (isPurple) return "animated purple";
        if (isGreen) return "animated green";

        return tileInfo ? "animated" : "";
    };

    // Se a consulta falhar, o site não trava: o backend recusa o pedido de qualquer forma.
    const salesStatusQuery = useSalesStatusQuery(date);
    const salesClosedMessage =
        salesStatusQuery.data?.online === false
            ? salesStatusQuery.data.message
            : null;

    const updateCalendar = calendarStore.update;
    useEffect(() => {
        if (salesClosedMessage) updateCalendar({ selectedSchedule: null });
    }, [salesClosedMessage, updateCalendar]);

    // Aviso só quando a pessoa clica na data (o checkout já inicia com hoje selecionado internamente).
    const setModalPage = useModal((state) => state.setPage);
    const [clickedDate, setClickedDate] = useState<string | null>(null);
    const initialSelectionChecked = useRef(false);

    useEffect(() => {
        const status = salesStatusQuery.data;
        if (
            !clearInitialDateWhenSalesClosed ||
            initialSelectionChecked.current ||
            !date ||
            clickedDate ||
            !status ||
            status.date !== getStringDate(date)
        ) {
            return;
        }

        initialSelectionChecked.current = true;
        if (!status.online) {
            setDate(null);
            calendarStore.update({ date: null, selectedSchedule: null });
        }
    }, [
        calendarStore,
        clearInitialDateWhenSalesClosed,
        clickedDate,
        date,
        salesStatusQuery.data,
    ]);

    useEffect(() => {
        const status = salesStatusQuery.data;
        if (!clickedDate || !status || status.date !== clickedDate) return;

        setClickedDate(null);
        if (status.online || !status.message) return;

        if (!salesClosedPopup) {
            toast.error(status.message, { toastId: `sales-closed-${clickedDate}` });
            return;
        }

        setModalPage({
            children: (
                <SalesClosedModal
                    formattedDate={smallDateFormatter.format(
                        dayjs(clickedDate).toDate()
                    )}
                    message={status.message}
                    handleClose={() => setModalPage(null)}
                />
            ),
        });
    }, [clickedDate, salesStatusQuery.data, salesClosedPopup, setModalPage]);

    const schedulesQuery = useSchedulesQuery(
        Array.isArray(calendarQuery.data) && !salesClosedMessage ? date : null
    );
    const availableSchedules = useMemo<string[]>(
        () => (Array.isArray(schedulesQuery.data) ? schedulesQuery.data : []),
        [schedulesQuery.data]
    );

    const currentSchedules = useMemo(() => {
        if (schedulesQuery.isPending || salesStatusQuery.isLoading) return [];

        return availableSchedules.map((d) => {
            const hours = d.split("-");
            const [hour1] = hours.map((h) => h.split(":"));
            const hour1Formatted = `${hour1[0]}:${hour1[1]}`;

            return `A partir das ${hour1Formatted}`;
        });
    }, [availableSchedules, schedulesQuery.isPending, salesStatusQuery.isLoading]);

    const handleSchoolDayContent = ({ date }: { date: Date }) => {
        const tileInfo = getTileInfoByDate(date);

        if (tileInfo?.season !== 1) return null;

        return (
            <a
                style={{
                    position: "absolute",
                    zIndex: 1,
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    // border: "2px solid red"
                    // background: "red",
                }}
                href="/escolas"
                target="_blank"
                onClick={e => e.stopPropagation()}
            ></a>
        );
    };

    const currentSchedulesIds = availableSchedules;

    const dateString = calendarStore.date
        ? smallDateFormatter.format(calendarStore.date)
        : "Sem data";

    let schedulesNullRenderer = "";
    if (schedulesQuery.isLoading || salesStatusQuery.isLoading)
        schedulesNullRenderer = "Carregando";
    else if (!date) schedulesNullRenderer = "Selecione uma data";
    else if (salesClosedMessage)
        schedulesNullRenderer =
            salesStatusQuery.data?.badgeLabel ?? "SEM HORÁRIO DISPONÍVEL";
    else if (!availableSchedules.length)
        schedulesNullRenderer = "Sem horários";
    else if (!calendarStore.selectedSchedule)
        schedulesNullRenderer = `Selecione o horário de chegada`;

    return (
        <>
            <Loading isLoading={calendarQuery.isLoading} />

            <ReactCalendarWrapper className={className}>
                <Calendar
                    prevLabel={<LuChevronLeft />}
                    nextLabel={<LuChevronRight />}
                    value={date}
                    onChange={(nextDate) => {
                        const selectedDate = Array.isArray(nextDate)
                            ? nextDate[0]
                            : nextDate;

                        initialSelectionChecked.current = true;
                        calendarStore.update({ date: selectedDate });
                        setDate(selectedDate);
                        setClickedDate(
                            selectedDate ? getStringDate(selectedDate) : null
                        );
                    }}
                    onActiveStartDateChange={(ctx) => {
                        setViewDate(ctx.activeStartDate);
                    }}
                    tileClassName={({ date }) => getTileClassName(date)}
                    tileDisabled={({ date }) => !getTileInfoByDate(date)}
                    tileContent={handleSchoolDayContent}
                />
                <div className="dropdown-wrapper">
                    <Dropdown
                        children={[dateString]}
                        ids={[dateString]}
                        divProps={{ style: { display: "none" } }}
                        setCurrentId={(id) => {
                            calendarStore.update({
                                selectedSchedule: id,
                            });
                        }}
                        icon={<LuCalendar />}
                        hideArrow={true}
                    />
                    <Dropdown
                        children={currentSchedules}
                        ids={currentSchedulesIds}
                        divProps={{}}
                        setCurrentId={(id) => {
                            const updatedDate = calendarStore.date
                                ? injectScheduleIntoDate(calendarStore.date, id)
                                : calendarStore.date;
                            calendarStore.update({
                                selectedSchedule: id,
                                ...(updatedDate ? { date: updatedDate } : {}),
                            });
                        }}
                        icon={<LuClock />}
                        nullRenderer={schedulesNullRenderer}
                    />
                </div>
                <div className="line"></div>
                <CalendarTags />
                {cartButton && (
                    <NavLink className="cart-link-button" to="/checkout">
                        Compre seu Ingresso
                    </NavLink>
                )}
            </ReactCalendarWrapper>
        </>
    );
};

export const CalendarModal = () => {
    const calendarStore = useCalendar();

    const isCalendarOpen =
        calendarStore.callback !== null && calendarStore.date !== null;

    const handleClose = () => {
        if (calendarStore.callback) calendarStore.callback(calendarStore.date);
        calendarStore.update({
            callback: null,
            title: null,
            sku: null,
        });
    };

    return createPortal(
        <AnimatePresence>
            {isCalendarOpen ? (
                <Backdrop
                    className="ignore-padding"
                    onClick={handleClose}
                    {...fadeInOut}
                >
                    <ModalContainer onClick={(e) => e.stopPropagation()}>
                        <CalendarModalHeadless
                            callback={(date) => {
                                if (calendarStore.callback)
                                    calendarStore.callback(date);
                            }}
                            initialDate={calendarStore.date}
                        />
                        <div className="buttons-wrapper">
                            <button className="animated" onClick={handleClose}>
                                Cancelar
                            </button>
                            <button
                                className="blue-background animated"
                                onClick={handleClose}
                            >
                                Salvar
                            </button>
                        </div>
                    </ModalContainer>
                </Backdrop>
            ) : null}
        </AnimatePresence>,
        document.getElementById("root")!
    );
};
